import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ShieldQuestion, Loader2, Check, Camera, RefreshCw, Lock, ChevronRight,
} from "lucide-react";

const ISSUE_LABEL = {
  no_context: "Needs context",
  no_identifier: "Unclear location",
  glare_or_blur: "Hard to read",
  missing_scale: "No size reference",
  coverage_gap: "Coverage gap",
  unpaired_angle: "Unpaired angle",
  single_shot: "Only one photo",
};

export function ChallengeProof({ propertyId, isLocked, onChanged }) {
  const navigate = useNavigate();
  const [review, setReview] = useState(null);
  const [frozen, setFrozen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/properties/${propertyId}/proof-review`);
      setReview(data.review || null);
      setFrozen(!!data.frozen);
    } catch (e) { console.error("proof-review load failed", e); }
    finally { setLoading(false); }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post(`/properties/${propertyId}/proof-review`);
      if (data.empty) { toast.info(data.message || "Add some photos first."); return; }
      setReview(data.review || null);
      setFrozen(!!data.frozen);
      const openN = (data.review?.items || []).filter((i) => i.status === "open").length;
      toast.success(openN ? `A skeptical reviewer flagged ${openN} thing${openN > 1 ? "s" : ""} to strengthen.` : "Nice — nothing stood out to a skeptical reviewer.");
    } catch (e) {
      if (e?.response?.status === 429) toast.error(e.response.data.detail);
      else if (e?.response?.status === 409) { toast.info("This evidence is sealed — coaching is read-only."); load(); }
      else toast.error(e?.response?.data?.detail || "Couldn't run the review. Please try again.");
    } finally { setRunning(false); }
  };

  const toggleResolved = async (item) => {
    try {
      const { data } = await api.patch(`/proof-review/items/${item.item_id}`, { resolved: !item.resolved });
      setReview(data.review || null);
      onChanged?.();
    } catch (e) {
      if (e?.response?.status === 409) toast.info(e.response.data.detail);
      else toast.error("Couldn't update that item.");
    }
  };

  if (loading) return null;

  const items = review?.items || [];
  const openItems = items.filter((i) => i.status === "open");
  const addressed = items.filter((i) => i.status === "resolved" || i.status === "superseded");
  const total = openItems.length + addressed.length;

  // Nothing to show yet, and locked with no review → hide entirely.
  if (!review && frozen) return null;

  return (
    <div data-testid="challenge-proof-card" className="rounded-2xl border border-[#E2E8F0] bg-white p-5 sm:p-6 theme-card">
      <div className="flex items-start gap-3">
        <span className="grid place-items-center h-10 w-10 rounded-xl bg-brand/10 text-brand shrink-0">
          <ShieldQuestion size={20} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading text-lg font-bold text-navy">Challenge my proof</h3>
            {!frozen && openItems.length > 0 && (
              <span data-testid="proof-reminder-badge"
                className="inline-flex items-center rounded-full bg-amber-soft border border-amber-border px-2 py-0.5 text-[11px] font-semibold text-amber-ink">
                {openItems.length} proof suggestion{openItems.length > 1 ? "s" : ""} to address
              </span>
            )}
          </div>
          <p className="mt-0.5 text-sm text-[#64748B] leading-snug">
            {frozen
              ? "Pre-lock coaching suggestions — kept as part of this record."
              : "A skeptical reviewer would ask… we check your photo set for fixable weak spots before you seal it."}
          </p>
        </div>
      </div>

      {total > 0 && (
        <div className="mt-4 flex items-center gap-3">
          <div className="h-2 flex-1 rounded-full bg-[#F1F5F9] overflow-hidden">
            <div className="h-full bg-brand transition-all" style={{ width: `${total ? (addressed.length / total) * 100 : 0}%` }} />
          </div>
          <span data-testid="challenge-proof-progress" className="text-xs font-semibold text-[#475569] shrink-0">
            {addressed.length} of {total} addressed
          </span>
        </div>
      )}

      {items.length > 0 && (
        <ul className="mt-4 space-y-2.5">
          {[...openItems, ...addressed].map((it) => {
            const done = it.status !== "open";
            return (
              <li key={it.item_id} data-testid={`proof-item-${it.item_id}`}
                className={`rounded-xl border p-3.5 ${done ? "border-[#E2E8F0] bg-[#F8FAFC] opacity-80" : "border-amber-border bg-amber-soft"}`}>
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${done ? "bg-[#E2E8F0] text-[#64748B]" : "bg-amber/20 text-amber-ink"}`}>
                        {ISSUE_LABEL[it.issue_type] || it.issue_type}
                      </span>
                      {it.room_name && <span className="text-[11px] text-[#94A3B8]">{it.room_name}</span>}
                      {it.status === "superseded" && <span className="text-[11px] text-[#94A3B8]">reappeared</span>}
                    </div>
                    <p className={`mt-1.5 text-sm ${done ? "text-[#64748B] line-through" : "text-navy font-medium"}`}>{it.message}</p>
                    {!done && <p className="mt-1 text-xs text-[#64748B]">{it.suggestion}</p>}
                    {!done && !frozen && it.room_id && (
                      <button data-testid={`proof-deeplink-${it.item_id}`}
                        onClick={() => navigate(`/capture/${propertyId}/${it.room_id}`)}
                        className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-brand hover:underline">
                        <Camera size={13} /> Retake / add photo <ChevronRight size={13} />
                      </button>
                    )}
                  </div>
                  {!frozen && (
                    <button data-testid={`proof-resolve-${it.item_id}`} onClick={() => toggleResolved(it)}
                      className={`shrink-0 grid place-items-center h-7 w-7 rounded-lg border transition-colors ${done ? "border-[#177245] bg-[#177245] text-white" : "border-[#CBD5E1] text-[#94A3B8] hover:border-brand hover:text-brand"}`}
                      title={done ? "Mark as open" : "Mark addressed"}>
                      <Check size={15} />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {review && openItems.length === 0 && addressed.length > 0 && (
        <p className="mt-4 text-sm text-[#177245] font-medium">All coaching suggestions addressed. 👏</p>
      )}

      {!frozen && (
        <div className="mt-4">
          <Button data-testid="challenge-proof-run-btn" onClick={run} disabled={running}
            variant={review ? "outline" : "default"}
            className={review ? "h-11 rounded-xl border-[#E2E8F0] text-navy hover:bg-[#F8FAFC]" : "h-11 rounded-xl bg-brand hover:bg-brand-hover text-white"}>
            {running ? <><Loader2 size={16} className="mr-2 animate-spin" /> Reviewing your photos…</>
              : review ? <><RefreshCw size={15} className="mr-2" /> Re-run review</>
              : <><ShieldQuestion size={16} className="mr-2" /> Challenge my proof</>}
          </Button>
        </div>
      )}

      {frozen && (
        <p className="mt-4 inline-flex items-center gap-1.5 text-xs text-[#94A3B8]">
          <Lock size={12} /> This review is frozen because the evidence is sealed.
        </p>
      )}
    </div>
  );
}
